import React, { useEffect, useState } from 'react';
import {
  useConfig,
  useFetchEffect,
  useFormRequest,
  useIntl,
} from '@recurrent/shared/build/hooks';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { HeadsetMic } from '@material-ui/icons';
import { getErrorMessage } from '@recurrent/shared/build/utils/response';
import { AlertMessage, Translated } from '@recurrent/shared/build/components';
import {
  fetchLinks,
  setAuthorizationHeader,
} from '@recurrent/shared/build/api';

import {
  login as apiLoginRequest,
  fetchReactivationCapability,
} from '../../api';

const defaultDataState = {
  username: '',
  password: '',
};

const LoginForm = ({ onSuccess }) => {
  const intl = useIntl();
  const { brandId } = useConfig();

  const { data: supportLink } = useFetchEffect({
    apiFetchFunction: () => fetchLinks(brandId),
    transformResponse: (response) => {
      return response.support_link;
    },
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [renderSupportButton, setRenderSupportButton] = useState(false);
  const [isAuthenticated, setAuthenticationState] = useState(false);

  const {
    requestInProgress: fetchingChargeInfo,
    onFormSubmit: checkChargeable,
  } = useFormRequest({
    defaultDataState: false,
    apiRequest: () => fetchReactivationCapability(),
    transformResponse: (response) => {
      return response.should_pay_for_reactivation;
    },
    onSuccess: (apiIsChargeable) => {
      if (!apiIsChargeable) {
        const translatedMessage = intl.formatMessage({
          id: 'user_is_not_chargeable',
        });

        setErrorMessage(translatedMessage);
        setRenderSupportButton(true);
        setAuthenticationState(false);
      } else {
        onSuccess();
      }
    },
  });

  const {
    data,
    mutateData,
    onFormSubmit: login,
    requestInProgress,
  } = useFormRequest({
    onSuccess: (authData) => {
      setAuthorizationHeader(authData.access_token);
      setAuthenticationState(true);
    },
    defaultDataState,
    apiRequest: () =>
      apiLoginRequest({
        ...data,
        payment_request: 1,
      }),
    onError: (error) => {
      setErrorMessage(
        intl.formatMessage({
          id: getErrorMessage(error),
        })
      );
      setRenderSupportButton(false);
    },
  });

  const onSubmit = (event) => {
    event.preventDefault();
    login();
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkChargeable();
    }
  }, [isAuthenticated]);

  const navigateToSupportPage = () => {
    window.open(supportLink, '_blank');
  };

  const supportButton = (
    <Box mt={2}>
      <Button
        fullWidth
        size="small"
        startIcon={<HeadsetMic />}
        color="primary"
        variant="contained"
        onClick={navigateToSupportPage}
      >
        <Translated id="contact_support" />
      </Button>
    </Box>
  );

  return (
    <form onSubmit={onSubmit}>
      <Typography component="div">
        <Translated id="payment_page_intro" />
      </Typography>

      {errorMessage && (
        <Box py={2}>
          <AlertMessage>
            {errorMessage}

            {renderSupportButton && supportButton}
          </AlertMessage>
        </Box>
      )}

      <TextField
        fullWidth
        type="email"
        margin="normal"
        placeholder="Email"
        value={data.username}
        disabled={requestInProgress}
        onChange={(e) => mutateData({ username: e.target.value })}
      />

      <TextField
        fullWidth
        margin="normal"
        type="password"
        value={data.password}
        placeholder="Password"
        disabled={requestInProgress}
        onChange={(e) => mutateData({ password: e.target.value })}
      />

      <Box mt={2}>
        <Button
          fullWidth
          type="submit"
          color="primary"
          variant="contained"
          disabled={requestInProgress || fetchingChargeInfo}
        >
          <Translated id="reopen_account" />
        </Button>
      </Box>
    </form>
  );
};

export default LoginForm;
